import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { AnalyticHooks, Stakeholder, useLocale, useReactForm } from '../../../hooks/index';
import { PossibleAction } from '../../../types/google-analytics-types.types';
import { b2bStakeholderTypes } from '../../../types/stakeholder.types';
import { handleFormInvalid } from '../../../utils/handle-form-invalid.util';
import { transformDateToISOString } from '../stakeholders';
import { ChildrenFormProps } from './stakeholder-actions.types';
import { FormSchema, formSchema } from './stakeholder-actions.validation';
import { FinalStep, StepOne, StepTwo } from './steps/index';

export type AddStakeholderModalProps = {
  companyId: string;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  setOpen: (state: boolean) => void;
};

const formSteps: ((props: ChildrenFormProps) => ReactNode)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <FinalStep {...props} />,
];

const AddStakeholderModal: FC<AddStakeholderModalProps> = ({
  isOpenModal,
  setOpen,
  companyId,
  invalidateQuery,
}) => {
  const { messagesLocale } = useLocale();
  const [currentStep, setCurrentStep] = useState(1);
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    resetField,
    clearErrors,
    setError,
    getFieldState,
    getValues,
    formState: { disabled, errors },
  } = useReactForm({
    schema: formSchema,
    defaultValues: {
      stepOne: {
        access: {
          capTable: true,
          companyTable: true,
          equity: true,
          position: true,
        },
      },
    },
  });

  const email = useWatch({ control, name: 'stepOne.email' });

  const { stakeholder: stakeholderByEmail, isLoading } = Stakeholder.useStakeholderByEmail({
    email,
  });

  useEffect(() => {
    if (!stakeholderByEmail) return;

    if (stakeholderByEmail.accessRole)
      return setError('stepOne.email', {
        message: messagesLocale['EMAIL_TAKEN_NEED_TO_CONVERT_GUEST_USER'],
      });
  }, [messagesLocale, setError, stakeholderByEmail]);

  const handleCloseModal = useCallback(() => {
    reset();
    setOpen(false);
    setCurrentStep(1);
  }, [reset, setOpen]);

  const addStakeholderTracker = AnalyticHooks.useClick(PossibleAction.ADD_STAKEHOLDER);
  const { createCompany, createHuman, isPendingCompany, isPendingHuman } = Stakeholder.useCreate();

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepTwo } = data;
      const {
        address,
        email,
        fullName,
        stakeholderType,
        phoneNumber,
        access,
        phoneCountry,
        company,
        human,
        subEmails,
      } = stepOne;
      if (b2bStakeholderTypes.includes(stakeholderType)) {
        createHuman(
          {
            companyId,
            data: {
              birthday: human?.birthdayDate
                ? transformDateToISOString(
                    human?.birthdayDate.year,
                    human?.birthdayDate.month + 1,
                    human?.birthdayDate.day,
                  )
                : undefined,
              address: address || undefined,
              email,
              fullName,
              nationality: human?.nationality?.iso2Code || undefined,
              passportExpDate: human?.passportExpiryDate
                ? transformDateToISOString(
                    human?.passportExpiryDate.year,
                    human?.passportExpiryDate.month + 1,
                    human?.passportExpiryDate.day,
                  )
                : undefined,
              passportNo: human?.passportNo.toString() || undefined,
              type: stakeholderType,
              phone: phoneCountry?.prefix ? phoneCountry?.prefix + phoneNumber : undefined,
              capTableAccess: access?.capTable,
              positionAccess: access?.position,
              capitalChangeAccess: access?.equity,
              companyTableAccess: access?.companyTable,
              additionalNotes: stepTwo?.additionalNotes,
              filesLinks:
                stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            },
          },
          {
            onSuccess: () => {
              addStakeholderTracker('Natural persons');
              handleCloseModal();
              toast.success(<AppFormattedMessage id="STAKEHOLDER_SUCCESSFULLY_CREATED" />);
              invalidateQuery();
            },
          },
        );

        return;
      }

      if (!company) return;
      return createCompany(
        {
          companyId,
          data: {
            email,
            stakeholderCompanyId: company?.identifier.toString() || undefined,
            fullName,
            countryOfInc: company?.incorporationCountry?.iso2Code || undefined,
            incDate: company?.incorporationDate
              ? transformDateToISOString(
                  company?.incorporationDate.year,
                  company?.incorporationDate.month + 1,
                  company?.incorporationDate.day,
                )
              : undefined,
            address: address || undefined,
            type: stakeholderType,
            phone: phoneCountry?.prefix ? phoneCountry?.prefix + phoneNumber : undefined,
            phoneCountryIso2: phoneCountry?.iso2Code || undefined,
            capTableAccess: access?.capTable,
            positionAccess: access?.position,
            capitalChangeAccess: access?.equity,
            companyTableAccess: access?.companyTable,
            additionalNotes: stepTwo?.additionalNotes,
            subEmails: subEmails.map(({ value }) => value),
            filesLinks:
              stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
          },
        },
        {
          onSuccess: () => {
            addStakeholderTracker('Legal entities');
            handleCloseModal();
            toast.success(<AppFormattedMessage id="STAKEHOLDER_SUCCESSFULLY_CREATED" />);
            invalidateQuery();
          },
        },
      );
    },
    [
      createCompany,
      companyId,
      createHuman,
      addStakeholderTracker,
      handleCloseModal,
      invalidateQuery,
    ],
  );

  const handleNextStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep === Object.keys(formSteps).length + 1) {
      handleSubmit(submitHandler, handleFormInvalid)();
      return;
    }
    if (nextStep > Object.keys(formSteps).length) return;

    setCurrentStep(nextStep);
  }, [currentStep, handleSubmit, submitHandler]);

  const handlePrevStep = useCallback(() => {
    const prevStep = currentStep - 1;
    if (prevStep < 1) return;
    setCurrentStep(prevStep);
  }, [currentStep]);

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="ADD_STAKEHOLDER" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex w-full items-center gap-[5px] bg-white px-6 pb-6">
            {formSteps.map((_, i) => (
              <div
                className={twMerge(
                  'h-1 w-full rounded-[25px]',
                  i + 1 <= currentStep ? 'bg-[#12B76A]' : 'bg-gray-100',
                  i + 1 === currentStep &&
                    currentStep === formSteps.length &&
                    'bg-gradient-to-r from-forest-500 to-forest-300',
                )}
                key={`${i}_${companyId}`}
              />
            ))}
          </div>
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {formSteps[currentStep - 1]({
              formData: getValues,
              nextFormStep: handleNextStep,
              prevFormStep: handlePrevStep,
              setFormData: setValue,
              control,
              clearErrors,
              handleCloseModal,
              setError,
              filedState: getFieldState,
              errors,
              disabled,
              resetField,
              isLoading: isPendingCompany || isPendingHuman || isLoading,
              stakeholder: null,
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddStakeholderModal;
